import { useNegotiateInfluencerCost } from "@entities/brief/lib/hooks/useNegotiateInfluencerCost";
import { ToastService } from "@shared/lib/services";
import { IBriefCampaignBriefInfluencer } from "@shared/lib/types/briefCampains";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { usePopoverInputFocus } from "@shared/lib/utils/focusPopoverInput";
import {
  formatInrDisplayAmount,
  parseInrAmountInput,
  sanitizeInrAmountInput,
} from "@shared/lib/utils/inrAmountInWords";
import { Alert, Button, Input, InrAmountWordsLine, Popover } from "@shared/ui";
import { ReactNode, useEffect, useState } from "react";
import styles from "./styles.module.scss";

type NegotiateInfluencerCommercialsPopoverProps = {
  briefInfluencer: IBriefInfluencerV2 | IBriefCampaignBriefInfluencer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

export function NegotiateInfluencerCommercialsPopover({
  briefInfluencer,
  open,
  onOpenChange,
  children,
}: NegotiateInfluencerCommercialsPopoverProps) {
  const handleClose = () => onOpenChange(false);

  const handleOpenChange = (visible: boolean) => {
    if (!visible) {
      onOpenChange(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="right"
      destroyTooltipOnHide
      overlayInnerStyle={{ padding: 12 }}
      content={
        open ? (
          <NegotiateInfluencerCommercialsPopoverContent
            briefInfluencer={briefInfluencer}
            onClose={handleClose}
          />
        ) : null
      }
    >
      <span>{children}</span>
    </Popover>
  );
}

type NegotiateInfluencerCommercialsPopoverContentProps = {
  briefInfluencer: IBriefInfluencerV2 | IBriefCampaignBriefInfluencer;
  onClose: () => void;
};

function NegotiateInfluencerCommercialsPopoverContent({
  briefInfluencer,
  onClose,
}: NegotiateInfluencerCommercialsPopoverContentProps) {
  const {
    negotiateInfluencerCost,
    isNegotiatingInfluencerCost,
    isNegotiatedInfluencerCost,
  } = useNegotiateInfluencerCost(briefInfluencer.briefId);

  const [value, setValue] = useState("");
  const targetCostInputRef = usePopoverInputFocus();

  const pricingQuote = formatInrDisplayAmount(
    briefInfluencer.influencerCost?.cost
  );

  const isPricingPocAssigned = Boolean(
    briefInfluencer?.assignedPricingPocV2?.assignedToUserId
  );

  useEffect(() => {
    if (isNegotiatedInfluencerCost) {
      onClose();
    }
  }, [isNegotiatedInfluencerCost, onClose]);

  const handleSubmit = () => {
    const targetInfluencerCost = parseInrAmountInput(value);

    if (targetInfluencerCost == null) {
      ToastService.error("Please enter target influencer commercial");
      return;
    }

    negotiateInfluencerCost({
      briefInfluencerId: briefInfluencer._id,
      targetInfluencerCost,
    });
  };

  return (
    <div
      className={styles.popover}
      onClick={(event) => event.stopPropagation()}
    >
      <h4 className={styles.title}>Request negotiation</h4>
      <p className={styles.description}>
        Brand manager target sent to your assigned pricing POC in{" "}
        <span className={styles.descriptionHighlight}>My Pricing</span>.
      </p>

      {!isPricingPocAssigned ? (
        <Alert
          className={styles.warning}
          message="Pricing POC not assigned. Please assign a Pricing POC to continue."
          type="warning"
          showIcon
        />
      ) : null}

      {pricingQuote ? (
        <div className={styles.pricingQuoteBlock}>
          <p className={styles.pricingQuoteLabel}>
            Commercials from pricing team
          </p>
          <div>
            <p className={styles.pricingQuoteAmount}>₹{pricingQuote}</p>
            <InrAmountWordsLine
              value={pricingQuote}
              className={styles.pricingQuoteWords}
            />
          </div>
        </div>
      ) : null}

      <label className={styles.fieldLabel} htmlFor="negotiate-target-cost">
        Requested negotiated cost (INR)
      </label>
      <div className={styles.amountInputWrap}>
        <Input
          ref={targetCostInputRef}
          id="negotiate-target-cost"
          inputMode="numeric"
          value={value}
          prefix="₹"
          onChange={(event) =>
            setValue(sanitizeInrAmountInput(event.target.value))
          }
          onPressEnter={handleSubmit}
          className={styles.amountInput}
          placeholder="e.g. 1,80,000"
        />
      </div>
      <InrAmountWordsLine value={value} className={styles.mutedText} />

      <div className={styles.actions}>
        <Button type="text" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          loading={isNegotiatingInfluencerCost}
          disabled={!value.trim() || !isPricingPocAssigned}
          onClick={handleSubmit}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
